import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GitBranch, RefreshCw, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button, ErrorState, Panel, Spinner } from '../../components/ui'
import { useAuth } from '../../components/auth-context'
import { auth } from '../../lib/auth'
import {
  deleteOrgSecret,
  bindWorkspaceGroup,
  discoverOrgWorkspaces,
  fetchIdentityGroups,
  fetchGitHubBranches,
  fetchGitHubRepositories,
  fetchOrgWorkspaces,
  fetchOrgSecrets,
  fetchOrgSettings,
  saveGitHubRepository,
  syncIdentityGroups,
  unbindWorkspaceGroup,
  updateOrgSettings,
  upsertOrgSecret,
  type IdentityGroup,
  type OrgSettings,
  type OrgSecret,
  type UpdateOrgSettingsBody,
  type Workspace,
} from '../../lib/orgApi'

type SettingsForm = {
  name: string
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

function toBody(form: SettingsForm): UpdateOrgSettingsBody {
  return {
    name: form.name,
  }
}

function formFromOrg(org: OrgSettings): SettingsForm {
  return {
    name: org.name,
  }
}

export function SettingsPage() {
  const session = useAuth()
  const isAdmin = session.organization?.role === 'admin'
  const queryClient = useQueryClient()
  const [secretKey, setSecretKey] = useState('')
  const [secretValue, setSecretValue] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const githubStatus = params.get('github')
    if (!githubStatus) return
    if (githubStatus === 'connected') {
      toast.success('GitHub App connected')
      auth.refreshSession()
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] })
      void queryClient.invalidateQueries({ queryKey: ['org-github-repositories'] })
    } else if (githubStatus === 'requested') {
      toast.info('GitHub App install requested')
    } else if (githubStatus === 'error') {
      toast.error(params.get('github_error') || "Couldn't connect GitHub")
    }
    params.delete('github')
    params.delete('github_error')
    const nextSearch = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`)
  }, [queryClient])

  const settingsQuery = useQuery({
    queryKey: ['org-settings'],
    queryFn: fetchOrgSettings,
    enabled: isAdmin,
  })

  const secretsQuery = useQuery({
    queryKey: ['org-secrets'],
    queryFn: fetchOrgSecrets,
    enabled: isAdmin,
  })

  const groupsQuery = useQuery({
    queryKey: ['identity-groups'],
    queryFn: fetchIdentityGroups,
    enabled: isAdmin,
  })

  const workspacesQuery = useQuery({
    queryKey: ['org-workspaces'],
    queryFn: fetchOrgWorkspaces,
    enabled: isAdmin,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const saveSettings = useMutation({
    mutationFn: (nextForm: SettingsForm) => updateOrgSettings(toBody(nextForm)),
    onSuccess: (org) => {
      queryClient.setQueryData(['org-settings'], org)
      auth.refreshSession()
      toast.success('Organization settings saved')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save settings"),
  })

  const saveSecret = useMutation({
    mutationFn: () => upsertOrgSecret(secretKey, secretValue),
    onSuccess: () => {
      setSecretKey('')
      setSecretValue('')
      void queryClient.invalidateQueries({ queryKey: ['org-secrets'] })
      toast.success('Secret saved')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save secret"),
  })

  const removeSecret = useMutation({
    mutationFn: (key: string) => deleteOrgSecret(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-secrets'] })
      toast.success('Secret deleted')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't delete secret"),
  })

  const discoverWorkspaces = useMutation({
    mutationFn: discoverOrgWorkspaces,
    onSuccess: (workspaces) => {
      queryClient.setQueryData(['org-workspaces'], workspaces)
      auth.refreshSession()
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] })
      toast.success('Workspaces discovered')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't discover workspaces"),
  })

  const syncGroups = useMutation({
    mutationFn: syncIdentityGroups,
    onSuccess: (groups) => {
      queryClient.setQueryData(['identity-groups'], groups)
      auth.refreshSession()
      void queryClient.invalidateQueries({ queryKey: ['org-workspaces'] })
      toast.success('Access groups synced')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't sync access groups"),
  })

  const bindGroup = useMutation({
    mutationFn: ({ workspaceKey, groupId }: { workspaceKey: string; groupId: string }) => bindWorkspaceGroup(workspaceKey, groupId),
    onSuccess: () => {
      auth.refreshSession()
      void queryClient.invalidateQueries({ queryKey: ['org-workspaces'] })
      toast.success('Access group bound')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't bind access group"),
  })

  const unbindGroup = useMutation({
    mutationFn: ({ workspaceKey, groupId }: { workspaceKey: string; groupId: string }) => unbindWorkspaceGroup(workspaceKey, groupId),
    onSuccess: () => {
      auth.refreshSession()
      void queryClient.invalidateQueries({ queryKey: ['org-workspaces'] })
      toast.success('Access group unbound')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't unbind access group"),
  })

  const onSaveSecret = (e: FormEvent) => {
    e.preventDefault()
    saveSecret.mutate()
  }

  if (!isAdmin) {
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col bg-app-bg">
        <PageHeader crumbs={[{ label: 'Organization' }]} />
        <div className="flex-1 overflow-auto p-4">
          <Panel title="Access denied">
            <p className="text-sm text-text-muted">Admin role required.</p>
          </Panel>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-app-bg">
      <PageHeader crumbs={[{ label: 'Organization' }]} />
      <div className="flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <Panel title="Organization">
            {settingsQuery.isLoading ? (
              <Spinner />
            ) : settingsQuery.isError ? (
              <ErrorState message="Couldn't load organization settings." />
            ) : settingsQuery.data ? (
              <OrganizationSettingsForm
                key={`${settingsQuery.data.id}:${settingsQuery.data.updatedAt}`}
                org={settingsQuery.data}
                saving={saveSettings.isPending}
                onSave={(nextForm) => saveSettings.mutate(nextForm)}
              />
            ) : (
              <Spinner />
            )}
          </Panel>

          <Panel
            title="Repository"
            actions={
              <Button variant="primary" onClick={() => window.location.assign('/api/org/github/install')}>
                <GitBranch size={14} />
                Configure GitHub repo
              </Button>
            }
          >
            {settingsQuery.data ? (
              <GitHubRepositoryPanel org={settingsQuery.data} />
            ) : (
              <Spinner />
            )}
          </Panel>

          <Panel title="Secrets">
            <form onSubmit={onSaveSecret} className="mb-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
              <label className="flex min-w-0 items-center justify-between gap-3">
                <span className="shrink-0 text-sm text-text-muted">Key</span>
                <input
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder="OPENAI_API_KEY"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                />
              </label>
              <label className="flex min-w-0 items-center justify-between gap-3">
                <span className="shrink-0 text-sm text-text-muted">Value</span>
                <input
                  value={secretValue}
                  onChange={(e) => setSecretValue(e.target.value)}
                  type="password"
                  placeholder="Secret value"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                />
              </label>
              <Button type="submit" className="justify-center whitespace-nowrap" disabled={saveSecret.isPending || !secretKey.trim() || !secretValue.trim()}>
                {saveSecret.isPending ? 'Saving...' : 'Save'}
              </Button>
            </form>
            {secretsQuery.isLoading ? (
              <Spinner />
            ) : secretsQuery.isError ? (
              <ErrorState message="Couldn't load organization secrets." />
            ) : (
              <SecretsTable rows={secretsQuery.data ?? []} busyKey={removeSecret.variables} onDelete={(key) => removeSecret.mutate(key)} />
            )}
          </Panel>

          <Panel
            title="Access"
            actions={
              <Button variant="primary" disabled={syncGroups.isPending} onClick={() => syncGroups.mutate()}>
                <RefreshCw size={14} />
                {syncGroups.isPending ? 'Syncing...' : 'Sync'}
              </Button>
            }
          >
            {groupsQuery.isLoading ? (
              <Spinner />
            ) : groupsQuery.isError ? (
              <ErrorState message="Couldn't load access groups." />
            ) : (
              <GroupsTable rows={groupsQuery.data ?? []} />
            )}
          </Panel>

          <Panel
            title="Workspaces"
            actions={
              <Button variant="primary" disabled={discoverWorkspaces.isPending} onClick={() => discoverWorkspaces.mutate()}>
                <RefreshCw size={14} />
                {discoverWorkspaces.isPending ? 'Discovering...' : 'Discover'}
              </Button>
            }
          >
            {workspacesQuery.isLoading ? (
              <Spinner />
            ) : workspacesQuery.isError ? (
              <ErrorState message="Couldn't load workspaces." />
            ) : (
              <WorkspacesTable
                rows={workspacesQuery.data ?? []}
                groups={groupsQuery.data ?? []}
                busy={bindGroup.isPending || unbindGroup.isPending}
                onBind={(workspaceKey, groupId) => bindGroup.mutate({ workspaceKey, groupId })}
                onUnbind={(workspaceKey, groupId) => unbindGroup.mutate({ workspaceKey, groupId })}
              />
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}

function OrganizationSettingsForm({
  org,
  saving,
  onSave,
}: {
  org: OrgSettings
  saving: boolean
  onSave: (form: SettingsForm) => void
}) {
  const [form, setForm] = useState<SettingsForm>(() => formFromOrg(org))

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSave(form)
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <Field label="Name">
        <input
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
        />
      </Field>
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={saving}>
          <Save size={14} />
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

function GitHubRepositoryPanel({ org }: { org: OrgSettings }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<{ owner?: string; repoName?: string; ref?: string }>({})

  const reposQuery = useQuery({
    queryKey: ['org-github-repositories'],
    queryFn: fetchGitHubRepositories,
    enabled: org.github.connected,
  })

  const repos = useMemo(() => reposQuery.data ?? [], [reposQuery.data])
  const owners = useMemo(() => Array.from(new Set(repos.map((repo) => repo.owner))).sort((a, b) => a.localeCompare(b)), [repos])
  const owner = draft.owner && repos.some((repo) => repo.owner === draft.owner)
    ? draft.owner
    : org.github.repoOwner && repos.some((repo) => repo.owner === org.github.repoOwner)
      ? org.github.repoOwner
      : repos[0]?.owner || ''
  const ownerRepos = useMemo(() => repos.filter((repo) => repo.owner === owner), [repos, owner])
  const repoName = draft.repoName && ownerRepos.some((repo) => repo.name === draft.repoName)
    ? draft.repoName
    : org.github.repoOwner === owner && org.github.repoName && ownerRepos.some((repo) => repo.name === org.github.repoName)
      ? org.github.repoName
      : ownerRepos[0]?.name || ''
  const selectedRepo = repos.find((repo) => repo.owner === owner && repo.name === repoName)

  const branchesQuery = useQuery({
    queryKey: ['org-github-branches', owner, repoName],
    queryFn: () => fetchGitHubBranches(owner, repoName),
    enabled: org.github.connected && !!owner && !!repoName,
  })
  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data])
  const branchNames = useMemo(() => new Set(branches.map((branch) => branch.name)), [branches])
  const preferredRef = org.github.repoOwner === owner && org.github.repoName === repoName ? org.github.repoRef : ''
  const ref = [draft.ref, preferredRef, selectedRepo?.defaultBranch, branches[0]?.name].find((candidate) => candidate && branchNames.has(candidate)) || draft.ref || preferredRef || selectedRepo?.defaultBranch || ''

  const saveRepo = useMutation({
    mutationFn: () => saveGitHubRepository({ owner, name: repoName, ref }),
    onSuccess: (nextOrg) => {
      queryClient.setQueryData(['org-settings'], nextOrg)
      auth.refreshSession()
      toast.success('Repository saved')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save repository"),
  })

  const onOwnerChange = (nextOwner: string) => {
    const firstRepo = repos.find((repo) => repo.owner === nextOwner)
    setDraft({ owner: nextOwner, repoName: firstRepo?.name || '', ref: firstRepo?.defaultBranch || 'main' })
  }

  const onRepoChange = (nextRepoName: string) => {
    const nextRepo = repos.find((repo) => repo.owner === owner && repo.name === nextRepoName)
    setDraft({ owner, repoName: nextRepoName, ref: nextRepo?.defaultBranch || 'main' })
  }

  const repository = org.github.repoOwner && org.github.repoName ? `${org.github.repoOwner}/${org.github.repoName}` : 'Not selected'
  const isSameSelection = org.github.repoOwner === owner && org.github.repoName === repoName && org.github.repoRef === ref
  const canSave = org.github.connected && !!owner && !!repoName && !!ref && !isSameSelection

  if (!org.github.connected) {
    return (
      <dl className="divide-y divide-gray-200">
        <DetailRow label="GitHub App" value="Not connected" />
        <DetailRow label="Repository" value={repository} />
        <DetailRow label="Reference" value={org.github.repoRef || '-'} />
      </dl>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <dl className="divide-y divide-gray-200">
        <DetailRow label="GitHub App" value="Connected" />
        <DetailRow label="Repository" value={repository} />
        <DetailRow label="Reference" value={org.github.repoRef || '-'} />
        <DetailRow label="Last check" value={fmtDate(org.repoCheck.checkedAt)} />
        <DetailRow label="Check status" value={org.repoCheck.status ?? '-'} />
        <DetailRow label="Synced commit" value={org.repoCheck.syncedCommitSha ?? '-'} />
      </dl>

      {reposQuery.isLoading ? (
        <Spinner />
      ) : reposQuery.isError ? (
        <ErrorState message="Couldn't load GitHub repositories." />
      ) : repos.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-200 p-6 text-center text-sm text-text-muted">No GitHub repositories available.</div>
      ) : (
        <form
          className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSave) saveRepo.mutate()
          }}
        >
          <SelectField label="Organization" value={owner} onChange={onOwnerChange}>
            {owners.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </SelectField>
          <SelectField label="Repository" value={repoName} onChange={onRepoChange}>
            {ownerRepos.map((repo) => (
              <option key={repo.fullName} value={repo.name}>
                {repo.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Reference branch"
            value={ref}
            onChange={(nextRef) => setDraft((current) => ({ ...current, owner, repoName, ref: nextRef }))}
            disabled={branchesQuery.isLoading || branches.length === 0}
          >
            {branchesQuery.isLoading ? (
              <option value={ref}>Loading...</option>
            ) : branchesQuery.isError ? (
              <option value={ref}>Unavailable</option>
            ) : branches.length === 0 ? (
              <option value="">No branches</option>
            ) : (
              branches.map((branch) => (
                <option key={branch.name} value={branch.name}>
                  {branch.name}
                </option>
              ))
            )}
          </SelectField>
          <Button type="submit" variant="primary" disabled={!canSave || saveRepo.isPending || branchesQuery.isError}>
            <GitBranch size={14} />
            {saveRepo.isPending ? 'Saving...' : 'Save'}
          </Button>
        </form>
      )}
    </div>
  )
}

function SelectField({
  label,
  value,
  onChange,
  disabled,
  children,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-xs font-medium text-text-muted">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm text-gray-700 outline-none focus:border-slate-400 disabled:text-gray-400"
      >
        {children}
      </select>
    </label>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex min-w-0 items-center justify-between gap-6">
      <span className="shrink-0 text-sm text-text-muted">{label}</span>
      <span className="flex min-w-0 w-full max-w-md justify-end">{children}</span>
    </label>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-6 py-3 first:pt-0 last:pb-0">
      <dt className="shrink-0 text-sm text-text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">{value}</dd>
    </div>
  )
}

function SecretsTable({ rows, busyKey, onDelete }: { rows: OrgSecret[]; busyKey?: string; onDelete: (key: string) => void }) {
  if (rows.length === 0) {
    return <div className="rounded-md border border-dashed border-gray-200 p-6 text-center text-sm text-text-muted">No secrets configured.</div>
  }
  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium text-text-muted">
          <tr>
            <th className="px-3 py-2">Key</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Updated</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((secret) => (
            <tr key={secret.key}>
              <td className="px-3 py-2 text-xs text-gray-900">{secret.key}</td>
              <td className="px-3 py-2 text-gray-600">{secret.configured ? 'configured' : secret.status}</td>
              <td className="px-3 py-2 text-gray-500">{fmtDate(secret.updatedAt)}</td>
              <td className="px-3 py-2 text-right">
                <Button
                  variant="ghost"
                  className="!text-red-600"
                  disabled={busyKey === secret.key}
                  onClick={() => onDelete(secret.key)}
                  title="Delete secret"
                >
                  <Trash2 size={14} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function GroupsTable({ rows }: { rows: IdentityGroup[] }) {
  if (rows.length === 0) {
    return <div className="rounded-md border border-dashed border-gray-200 p-6 text-center text-sm text-text-muted">No access groups synced.</div>
  }
  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium text-text-muted">
          <tr>
            <th className="px-3 py-2">Group</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">External ID</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((group) => (
            <tr key={group.id}>
              <td className="px-3 py-2 font-medium text-gray-900">{group.displayName}</td>
              <td className="px-3 py-2 text-gray-600">{group.groupEmail ?? '-'}</td>
              <td className="px-3 py-2 text-xs text-gray-500">{group.externalId}</td>
              <td className="px-3 py-2 text-gray-600">{group.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WorkspacesTable({
  rows,
  groups,
  busy,
  onBind,
  onUnbind,
}: {
  rows: Workspace[]
  groups: IdentityGroup[]
  busy: boolean
  onBind: (workspaceKey: string, groupId: string) => void
  onUnbind: (workspaceKey: string, groupId: string) => void
}) {
  if (rows.length === 0) {
    return <div className="rounded-md border border-dashed border-gray-200 p-6 text-center text-sm text-text-muted">No workspaces discovered.</div>
  }
  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium text-text-muted">
          <tr>
            <th className="px-3 py-2">Workspace</th>
            <th className="px-3 py-2">Root</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Access groups</th>
            <th className="px-3 py-2">Add group</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((workspace) => {
            const bound = workspace.groupBindings ?? []
            return (
              <tr key={workspace.id}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{workspace.displayName}</div>
                  <div className="text-xs text-gray-500">{workspace.key}</div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">{workspace.rootPath}</td>
                <td className="px-3 py-2 text-gray-600">{workspace.status}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1.5">
                    {bound.length === 0 ? (
                      <span className="text-text-muted">-</span>
                    ) : (
                      bound.map((group) => (
                        <button
                          key={group.identityGroupId}
                          type="button"
                          disabled={busy}
                          onClick={() => onUnbind(workspace.key, group.identityGroupId)}
                          className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                          title="Remove access group"
                        >
                          {group.displayName}
                        </button>
                      ))
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <select
                    value=""
                    disabled={busy || groups.length === 0}
                    onChange={(e) => {
                      if (e.target.value) onBind(workspace.key, e.target.value)
                    }}
                    className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-slate-400"
                  >
                    <option value="">Select access group</option>
                    {groups
                      .filter((group) => group.status === 'active' && !bound.some((b) => b.identityGroupId === group.id))
                      .map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.displayName}
                        </option>
                      ))}
                  </select>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
